import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { API_BASE_URL } from "../config/apiBase";
import { getAuthUserHeader } from "../utils/userRoles";
import { normalizeStudentId } from "../utils/studentRoutes";
import { downloadStudentCertificate } from "../utils/downloadStudentCertificate";
import "./LessonPage.css";
import { isStudentView } from "../utils/studentView";

export default function CourseCompletePage() {
  const params = useParams();
  const studentId = normalizeStudentId(params.studentId || params.id);
  const [state, setState] = useState(null);
  const [error, setError] = useState("");
  const [certificateLoading, setCertificateLoading] = useState(false);
  const api = API_BASE_URL;
  const readOnly = isStudentView();
  const pendingSteps = {
    w4l4: "Complete Week 4, Lesson 4.",
    week_4_reflection: "Submit your Week 4 reflection.",
    final_mentor: "Read and acknowledge your final Mentor review.",
  };
  async function requestEnding(path, method = "GET") {
    const response = await fetch(`${api}/api/student/course-end${path}?student_id=${encodeURIComponent(studentId || "")}`, {
      method,
      headers: { "X-User": getAuthUserHeader() },
    });
    const body = await response.json();
    if (!response.ok) throw new Error("course_end_request_failed");
    return body;
  }
  useEffect(() => {
    let cancelled = false;
    setError("");
    setState(null);
    requestEnding("").then((body) => {
      if (!cancelled) setState(body);
    }).catch(() => {
      if (!cancelled) setError("We could not load your course ending yet.");
    });
    return () => { cancelled = true; };
    // requestEnding uses only the API base and the current route identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, studentId]);
  const start = () => readOnly ? Promise.resolve() : requestEnding("/video/start", "POST").then(setState)
    .catch(() => setError("We could not start your closing video. Please try again."));
  const complete = () => readOnly ? Promise.resolve() : requestEnding("/video/complete", "POST").then(setState)
    .then((body) => {
      window.dispatchEvent(new CustomEvent("studentCourseVideoCompleted", { detail: { studentId } }));
      return body;
    }).catch(() => setError("We could not save your video completion. Please try again."));
  const downloadCertificate = async () => {
    setCertificateLoading(true);
    setError("");
    try {
      await downloadStudentCertificate(studentId);
    } catch (downloadError) {
      setError(downloadError.message || "We could not prepare your certificate. Please try again.");
    } finally {
      setCertificateLoading(false);
    }
  };
  return (
    <main className="lp-course-complete-screen" aria-label="Course complete">
      <div className="lp-course-complete-content">
        <h1>A final message for your writing journey</h1>
        {error ? <p>{error}</p> : !state ? <p>Loading…</p> : state.video_url ? <>
          <p>You have completed the taught course and your final reflection. Shaanzae has prepared this closing message for the stage you have reached in your development as a writer.</p>
          <button type="button" onClick={start} disabled={readOnly}>Begin video</button>
          <iframe title="Closing message" src={state.video_url.replace("youtu.be/", "www.youtube.com/embed/")} width="100%" height="360" allowFullScreen onLoad={start} />
          {!state.video_completed && <button type="button" onClick={complete} disabled={readOnly}>Mark video complete</button>}
          {state.video_completed && <><p>You have completed your HIRI learning journey. Your certificate is ready to download.</p><button type="button" onClick={downloadCertificate} disabled={certificateLoading}>{certificateLoading ? "Preparing certificate…" : "Download Certificate"}</button></>}
        </> : <section aria-label="Certificate requirements">
          <h2>Certificate not ready yet</h2>
          {state.missing?.length ? <>
            <p>These course steps are still required:</p>
            <ul>{state.missing.map((step) => <li key={step}>{pendingSteps[step] || "Finish the remaining course steps."}</li>)}</ul>
          </> : <p>{readOnly ? "The course ending has not been prepared yet. The student must open this page in their own account to prepare it." : "Your course ending is not available yet. Please contact your tutor."}</p>}
          {readOnly && <p>This preview cannot complete these steps for the student.</p>}
        </section>}
      </div>
    </main>
  );
}
